import {useInfiniteQuery, UseInfiniteQueryOptions} from '@tanstack/react-query'
import {httpGetPatients, HttpGetPatientsOptions} from './patients.http'
import {z} from 'zod'
import {PatientSmall} from '../types'
import {PaginationResponse} from '@/types'
import {useInputSearch} from '@/hooks/useInputSearch'
import {QUERY_KEYS} from '@/queryClient'
import {parseAxiosPromise} from '@/axiosInstance'
import {captureException} from '@sentry/react'
import {infiniteQueryFetchNextPage} from '@/utilities/helpers'

const ResponseShape = z.object({
    data: z.array(PatientSmall),
    meta: PaginationResponse
})
type ResponseShape = z.infer<typeof ResponseShape>

export const useInfinitePatients = (
    params: Omit<HttpGetPatientsOptions['params'], 'page'>,
    options?: Pick<UseInfiniteQueryOptions<ResponseShape>, 'enabled'>
) => {
    const {searchValue, onSearch, onResetSearch} = useInputSearch()
    const query = useInfiniteQuery({
        queryKey: [QUERY_KEYS.PATIENTS, {...params, search: params.search ?? searchValue}],
        queryFn: ({pageParam = 1}) =>
            parseAxiosPromise({
                axiosPromise: httpGetPatients({
                    params: {
                        ...params,
                        search: params.search ?? searchValue,
                        page: pageParam
                    }
                }),
                responseShape: ResponseShape,
                onZodError: captureException
            }),
        ...options,
        initialPageParam: 1,
        getNextPageParam: ({meta}) => (meta.currentPage < meta.lastPage ? meta.currentPage + 1 : null)
    })

    return {
        ...query,
        fetchNextPage: () => infiniteQueryFetchNextPage(query),
        remappedData: query.data?.pages[0]?.data?.length ? query.data.pages.flatMap(page => page.data) : [],
        searchValue,
        onResetSearch,
        onSearch
    }
}
